/**
 * The docs' page on results and proofs: the two public documents every campaign leaves, where
 * the escrow records the hash of each, how anyone recomputes a result with `morcast-verify`, and
 * how a creator's payout is claimed by proof. The statements follow the V1 specification
 * (docs/specification.md, sections 4.3, 9 and 10).
 */

import { styles } from "../../components/ui.js";
import { VerifyExample } from "../home/visuals.js";
import { PROTOCOL_URL } from "./nav.js";
import { Code, DocPage, DocSection, DocTable, P, Points } from "./parts.js";

export function Results() {
  return (
    <DocPage
      group="Protocol"
      title="Results and proofs"
      lead="Every campaign leaves two public documents, and the escrow holds the hash of each. Anyone can recompute a result from them and compare it with the chain."
    >
      <DocSection id="documents" title="The two documents">
        <DocTable
          caption="The documents of a campaign and when the hash of each goes on-chain"
          head={["Document", "What it holds", "When its hash goes on-chain"]}
          rows={[
            [
              "Manifest",
              "The campaign's terms, which are the brand's wallet, the token, the budget, the target, the dates, the brief, the limits, and the versions of the models and prompts",
              "At the deposit",
            ],
            [
              "Result dataset",
              "Every item with its status, reasons, reach and evidence. Every creator's total and payout. The totals and every payout leaf",
              "At the settlement",
            ],
          ]}
        />
        <Points
          items={[
            {
              lead: "Published before settlement.",
              text: "The result is published by Day 4. Creators and the brand can raise issues until Day 7, and a corrected result replaces the earlier one.",
            },
            {
              lead: "Kept beyond Morcast.",
              text: "Each document is served at a permanent public address and copied to Arweave, a permanent public archive.",
            },
          ]}
        />
      </DocSection>

      <DocSection id="verify" title="Checking a result yourself">
        <P>
          The open-source tool <Code>morcast-verify</Code> recomputes the hashes, the totals and
          every payout from the dataset, and compares them with the escrow. It is part of the{" "}
          <a href={PROTOCOL_URL} rel="noopener" className={styles.link}>
            Morcast Payment Protocol
          </a>
          .
        </P>
        {/* The example's glow reaches past its sides, which the home page's band clips. */}
        <div className="overflow-x-clip">
          <VerifyExample />
        </div>
      </DocSection>

      <DocSection id="claims" title="Claims">
        <Points
          items={[
            {
              lead: "Claimed by proof.",
              text: "A creator's payout is one leaf of a Merkle tree, and the escrow holds the tree's root.",
            },
            {
              lead: "Paid to the creator only.",
              text: "Anyone can submit the claim, and the money always goes to the creator's wallet. Morcast can pay the gas for creators.",
            },
            {
              lead: "Never expires.",
              text: "A payout waits in the escrow until it is claimed.",
            },
          ]}
        />
      </DocSection>
    </DocPage>
  );
}
